import { BaseModel } from "../../model/shared/BaseModel";
import {
  IMPORT_MAPPING_URL,
  PRODUCT_QUALITY_URL,
  WORKFLOW_URL,
} from "../../utils/helpers/api.routes";
import { requests } from "../../utils/helpers/api.services";
import { ICopywriterData } from "./components/WorkflowStep5";
import { IWorkflow } from "./dto/IWorkflow";

export const WorkflowService = {
  createWorkflow: (params: IWorkflow) =>
    requests.post(`${WORKFLOW_URL}`, params),
  getAllWorkflows: (params: any): Promise<BaseModel<IWorkflow[]>> =>
    requests.get(`${WORKFLOW_URL}`, params),
  getWorkflowProducts: (
    params: any,
    id: number
  ): Promise<BaseModel<IWorkflow[]>> =>
    requests.get(`${WORKFLOW_URL}/base-data/list/${id}`, params),
  getWorkflowMediaProducts: (
    params: any,
    id: number
  ): Promise<BaseModel<IWorkflow[]>> =>
    requests.get(`${WORKFLOW_URL}/media/list/${id}`, params),
  getWorkflowPreviewProducts: (
    params: any,
    id: number
  ): Promise<BaseModel<IWorkflow[]>> =>
    requests.get(`${WORKFLOW_URL}/preview/list/${id}`, params),
  getCopywritingProducts: (
    params: any,
    id: number
  ): Promise<BaseModel<IWorkflow[]>> =>
    requests.get(`${WORKFLOW_URL}/copywriting/list/${id}`, params),
  getNextAttributesWorkflow: (workflowId: number, stepId: string) =>
    requests.get(
      `${WORKFLOW_URL}/${
        stepId === "COPY_WRITING" ? "copywriting" : "attributes"
      }/next/${workflowId}`
    ),
  getNextBaseDataWorkflow: (workflowId: number) =>
    requests.get(`${WORKFLOW_URL}/base-data/next/${workflowId}`),
  getWorkflowAttributesProducts: (
    params: any,
    id: number
  ): Promise<BaseModel<IWorkflow[]>> =>
    requests.get(`${WORKFLOW_URL}/attributes/list/${id}`, params),
  getWorkflowExportProducts: (
    params: any,
    id: number
  ): Promise<BaseModel<IWorkflow[]>> =>
    requests.get(`${WORKFLOW_URL}/export/product-list/${id}`, params),
  getWorkflowById: (id: number) => requests.get(`${WORKFLOW_URL}/${id}`),
  getWorkflowArrowShow: (productId: number, workflowId: number) =>
    requests.get(
      `${WORKFLOW_URL}/workflow-product-switchable-tabs/${productId}/${workflowId}`
    ),
  getMappedImportStatistics: (id: number) =>
    requests.get(`${WORKFLOW_URL}/import/mapped-import-statistics/${id}`),
  getWorkflowCharts: (id: number) =>
    requests.get(`${WORKFLOW_URL}/overview/${id}`),
  updateWorkflowById: (id: number, params: IWorkflow) =>
    requests.put(`${WORKFLOW_URL}/${id}`, params),
  deleteWorkflowById: (id: number) => requests.delete(`${WORKFLOW_URL}/${id}`),
  workflowUpload: (file: any, id: number) =>
    requests.put(`${WORKFLOW_URL}/${id}/upload`, { file, id }),
  getNextWorkflow: (workflowId: number) =>
    requests.get(`${WORKFLOW_URL}/base-data/next/${workflowId}`),
  deleteWorkflowBaseData: (productId: number, workflowId: number) =>
    requests.delete(
      `${WORKFLOW_URL}/base-data/delete/${productId}/${workflowId}`
    ),
  deleteAttributeBaseDataById: (productId: number, workflowId: number) =>
    requests.delete(
      `${WORKFLOW_URL}/attributes/delete/${productId}/${workflowId}`
    ),
  saveWorkflowImportById: (id: number) =>
    requests.put(`${WORKFLOW_URL}/import/${id}`),
  assignCopywriter: (id: number, payload: ICopywriterData) =>
    requests.put(`${WORKFLOW_URL}/copywriting/assign-multiple/${id}`, payload),
  saveWorkflowBaseDataById: (id: number) =>
    requests.put(`${WORKFLOW_URL}/base-data/save/${id}`),
  saveWorkflowMediaById: (id: number) =>
    requests.put(`${WORKFLOW_URL}/media/base-data/save/${id}`),
  saveWorkflowAttributesById: (id: number) =>
    requests.put(`${WORKFLOW_URL}/attributes/save/${id}`),
  saveWorkflowCopywritingById: (id: number) =>
    requests.put(`${WORKFLOW_URL}/copywriting/save/${id}`),
  getNextTranslationWorkflow: (workflowId: number) =>
    requests.get(`${WORKFLOW_URL}/translation/next/${workflowId}`),
  saveWorkflowTranslationById: (id: number) =>
    requests.put(`${WORKFLOW_URL}/translation/save/${id}`),
  workflowComplete: (id: number) =>
    requests.put(`${WORKFLOW_URL}/export/save/${id}`),

  getTranslationProducts: (
    params: any,
    id: number
  ): Promise<BaseModel<IWorkflow[]>> =>
    requests.get(`${WORKFLOW_URL}/translation/list/${id}`, params),

  export: (id: number, params: any) =>
    requests.put(
      `${WORKFLOW_URL}/export/export-workflow-products/${id}`,
      params
    ),

  assignTranslator: (id: number, payload: ICopywriterData) =>
    requests.put(`${WORKFLOW_URL}/translation/assign-multiple/${id}`, payload),
  assignCopywriterMedia: (id: number, payload: ICopywriterData) =>
    requests.put(`${WORKFLOW_URL}/media/assign-multiple/${id}`, payload),
  assignTranslatorBaseData: (id: number, payload: ICopywriterData) =>
    requests.put(`${WORKFLOW_URL}/base-data/assign-multiple/${id}`, payload),
  assignTranslatorAttributes: (id: number, payload: ICopywriterData) =>
    requests.put(`${WORKFLOW_URL}/attributes/assign-multiple/${id}`, payload),
  getTranslationProductById: (workflowId: number, id: number) =>
    requests.get(
      `${WORKFLOW_URL}/translation/product-data/${workflowId}/${id}`
    ),
  updateTranslationProductById: (
    params: FormData,
    id: number,
    workflowId: number
  ) =>
    requests.put(
      `${WORKFLOW_URL}/translation/product-data/${workflowId}/${id}`,
      params
    ),
  changeFinishedStatusTranslation: (id: number, payload: any, status: string) =>
    requests.put(
      `${WORKFLOW_URL}/${status}/product-status-change/${id}`,
      payload
    ),
  changeFinishedStatusCopyWrite: (id: number, payload: any) =>
    requests.put(
      `${WORKFLOW_URL}/copywriting/product-status-change/${id}`,
      payload
    ),
  assignVariationToProduct: (payload: any) =>
    requests.put(
      `${WORKFLOW_URL}/copywriting/assign-variation-to-product`,
      payload
    ),
  changeFinishedStatusBase: (id: number, payload: any) =>
    requests.put(`${WORKFLOW_URL}/product-status-change/${id}`, payload),
  changeFinishedStatusMedia: (id: number, payload: any) =>
    requests.put(`${WORKFLOW_URL}/media/product-status-change/${id}`, payload),
  changeFinishedStatusAtt: (id: number, payload: any) =>
    requests.put(
      `${WORKFLOW_URL}/attributes/product-status-change/${id}`,
      payload
    ),
  refreshAllAttributes: (id: number) =>
    requests.put(`${WORKFLOW_URL}/attributes/refresh/${id}`),
  workflowTranslateAll: (id: number, params: any) =>
    requests.put(
      `${WORKFLOW_URL}/translation/translate-all-products/${id}`,
      params
    ),
  baseDataTranslateAll: (id: number) =>
    requests.put(`${WORKFLOW_URL}/translate-base-data-all-products/${id}`),
  createImportMapping: (params: any, workflowId: number) =>
    requests.post(`${IMPORT_MAPPING_URL}create/${workflowId}`, params),
  newUpdateImportMapping: (params: any, workflowId: number) =>
    requests.post(`${IMPORT_MAPPING_URL}update/${workflowId}`, params),
  newMapImportMapping: (params: any, workflowId: number) =>
    requests.post(`${IMPORT_MAPPING_URL}enrich/${workflowId}`, params),
  getAllImportMapping: (workflowId: number) =>
    requests.get(`${IMPORT_MAPPING_URL}list/${workflowId}`),
  updateImportMapping: (params: any, profileId: number) =>
    requests.put(`${IMPORT_MAPPING_URL}update/${profileId}`, params),
  getProfile: (profileId: number) =>
    requests.get(`${IMPORT_MAPPING_URL}detail/${profileId}`),
  previewImportMapping: (params: any) =>
    requests.post(`${IMPORT_MAPPING_URL}preview`, params),
  importMapping: (params: any, workflowId: number) =>
    requests.post(
      `${IMPORT_MAPPING_URL}import-to-workflow/${workflowId}`,
      params
    ),
  exportProducts: (body: any) =>
    requests.put(`${WORKFLOW_URL}/products-add`, body!),
  generateAiVariations: (id: number) =>
    requests.put(`${WORKFLOW_URL}/generate-variations/${id}`),
  lockWorkflow: (id: number, params: any) =>
    requests.put(`${WORKFLOW_URL}/lock/lock/${id}`, params),
  unlockWorkflow: (id: number, params: any) =>
    requests.put(`${WORKFLOW_URL}/lock/unlock/${id}`, params),
  getWorkflowGeneralStatistics: (workflowId: number) =>
    requests.get(`${PRODUCT_QUALITY_URL}/general-statistics/${workflowId}`),
};
